<template>
  <div class="">
    <!-- Carousel Hero Section -->
    <HeroCarouselComp 
      :slides="synergySlides"
      background-image="/banner/banner-two.jpg"
      pattern-image="/shapes/shape-06.png"
      :mission-vision="synergyMissionVision"
    />

    <!-- Product Categories Section -->
    <SynergyItsProductComp :content="productCategoriesContent" />

    <!-- Featured Products Section -->
    <SynergyItsFeaturedComp :content="featuredProductsContent" />

    <!-- Development Process Section -->
    <SynergyItsProcessComp :content="developmentProcessContent" />
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import { WOW } from "wowjs";
import HeroCarouselComp from "@/components/HeroCarouselComp.vue";
import SynergyItsProductComp from "@/components/SynergyItsProductComp.vue";
import SynergyItsFeaturedComp from "@/components/SynergyItsFeaturedComp.vue";
import SynergyItsProcessComp from "@/components/SynergyItsProcessComp.vue";

export default {
  name: "HomeSynergyPage",
  components: {
    HeroCarouselComp,
    SynergyItsProductComp,
    SynergyItsFeaturedComp,
    SynergyItsProcessComp
  },
  setup() {
    const synergySlides = ref([
      {
        backgroundImage: '/banner/banner-two.jpg',
        subtitle: 'Synergy Across Sectors',
        titleLines: [
          { text: 'Software that ', highlight: 'Transforms Industries' }
        ],
        description: 'Sogdo is actively innovating in 10+ sectors with dedicated software products for each vertical.',
        buttonText: 'Contact Us',
        buttonLink: '/home-contact'
      },
      {
        backgroundImage: '/banner/banner-three.jpg',
        subtitle: 'Industry Solutions',
        titleLines: [
          { text: '10 Industry-wise ', highlight: 'Software Products' }
        ],
        description: 'From Healthcare to Fintech, Education to Manufacturing - we create specialized software solutions for each industry vertical.',
        buttonText: 'Learn More',
        buttonLink: '/home-contact'
      },
      {
        backgroundImage: '/news/news-1.jpg',
        subtitle: 'Technology Innovation',
        titleLines: [
          { text: 'Cutting-Edge ', highlight: 'Technology Stack' }
        ],
        description: 'Leveraging modern technologies including React, Node.js, Python, AI/ML, and cloud platforms to deliver exceptional software solutions.',
        buttonText: 'Explore Tech',
        buttonLink: '/home-contact'
      },
      {
        backgroundImage: '/news/news-2.jpg',
        subtitle: 'Research & Development',
        titleLines: [
          { text: 'Innovation Through ', highlight: 'Research Products' }
        ],
        description: 'Our R&D team works across diverse domains from AI to healthcare, developing breakthrough solutions that transform industries.',
        buttonText: 'View Research',
        buttonLink: '/home-research'
      },
      {
        backgroundImage: '/banner/banner-01.png',
        subtitle: 'Sogdo\'s Edge',
        titleLines: [
          { text: 'Industry Specific ', highlight: 'Teams for Industries' }
        ],
        description: 'Modular, scalable platforms with integration capabilities and built for speed & scale across all industry verticals.',
        buttonText: 'Get Started',
        buttonLink: '/home-contact'
      }
    ]);

    const synergyMissionVision = ref({
      vision: {
        title: 'Our Vision',
        description: 'To be the leading provider of cross-industry software solutions that create synergies and drive innovation.'
      },
      mission: {
        title: 'Our Mission',
        description: 'To develop and deliver software products that transform industries and create meaningful connections between different sectors.'
      }
    });

    const productCategoriesContent = ref({
      sectionLabel: "Better Future",
      sectionTitle: "Product Categories",
      products: [
        {
          title: "Enterprise Apps",
          description: "Comprehensive business solutions designed for large organizations with complex workflows and requirements.",
          icon: "icon-01",
          link: "/home-services"
        },
        {
          title: "SaaS Platforms",
          description: "Cloud-based software solutions that provide scalable, subscription-based services for modern businesses.",
          icon: "icon-02",
          link: "/home-services"
        },
        {
          title: "Internal Tools",
          description: "Custom tools and applications designed to streamline internal processes and improve team productivity.",
          icon: "icon-03",
          link: "/home-services"
        },
        {
          title: "Mobile Applications",
          description: "Native and cross-platform mobile apps that deliver exceptional user experiences across all devices.",
          icon: "icon-04",
          link: "/home-services"
        },
        {
          title: "Web Applications",
          description: "Modern web applications built with cutting-edge technologies for optimal performance and user experience.",
          icon: "icon-05",
          link: "/home-services"
        },
        {
          title: "AI Solutions",
          description: "Artificial intelligence and machine learning solutions that automate processes and provide intelligent insights.",
          icon: "icon-06",
          link: "/home-services"
        }
      ]
    });

    const featuredProductsContent = ref({
      sectionLabel: "Industry Showcases",
      sectionTitle: "10 Industry-wise",
      sectionTitleHighlight: "Software Product Showcases",
      products: [
        {
          title: "Healthcare",
          description: "Patient Management System, Remote Diagnostics, AI-powered Health Insights - comprehensive healthcare software solutions.",
          image: "/resource/portfolio-01.jpg",
          techStack: ["Vue.js", "Node.js", "MongoDB"],
          link: "/home-contact"
        },
        {
          title: "Fintech",
          description: "Digital Wallets, KYC Modules, Crypto Payment Gateway, Fraud Detection Systems - next-generation financial technology solutions.",
          image: "/resource/portfolio-02.jpg",
          techStack: ["React", "Python", "PostgreSQL"],
          link: "/home-contact"
        },
        {
          title: "Education",
          description: "LMS Systems, Virtual Classrooms, AI-Powered Assessment Tools - comprehensive educational technology solutions.",
          image: "/resource/portfolio-03.jpg",
          techStack: ["Flutter", "Firebase", "AWS"],
          link: "/home-contact"
        },
        {
          title: "Retail & E-commerce",
          description: "Multi-vendor Marketplace Platform, Order Tracking System, Loyalty Programs - comprehensive retail technology solutions.",
          image: "/resource/portfolio-04.jpg",
          techStack: ["Next.js", "Stripe", "Redis"],
          link: "/home-contact"
        },
        {
          title: "Logistics",
          description: "Real-Time Fleet Tracking, Inventory & Dispatch Tools - comprehensive logistics management solutions.",
          image: "/resource/portfolio-01.jpg",
          techStack: ["IoT", "GPS", "Analytics"],
          link: "/home-contact"
        },
        {
          title: "Automotive",
          description: "Vehicle Booking System, Garage Management Software - comprehensive automotive technology solutions.",
          image: "/resource/portfolio-02.jpg",
          techStack: ["Booking", "Management", "IoT"],
          link: "/home-contact"
        },
        {
          title: "AI & Automation",
          description: "Chatbots, Process Bots, NLP Engines - advanced artificial intelligence and automation solutions.",
          image: "/resource/portfolio-03.jpg",
          techStack: ["AI", "NLP", "ML"],
          link: "/home-contact"
        },
        {
          title: "Manufacturing",
          description: "Smart Factory Dashboard, IoT Integration Suite - comprehensive manufacturing technology solutions.",
          image: "/resource/portfolio-04.jpg",
          techStack: ["IoT", "Dashboard", "Analytics"],
          link: "/home-contact"
        },
        {
          title: "Real Estate",
          description: "Property Listing + CRM Software, Rent Management Apps - comprehensive real estate technology solutions.",
          image: "/resource/portfolio-01.jpg",
          techStack: ["CRM", "Mobile", "Analytics"],
          link: "/home-contact"
        },
        {
          title: "Market Research",
          description: "Survey Tools, Dashboards, Data Visualization Systems - comprehensive market research technology solutions.",
          image: "/resource/portfolio-02.jpg",
          techStack: ["Analytics", "Visualization", "Survey"],
          link: "/home-contact"
        }
      ]
    });

    const developmentProcessContent = ref({
      sectionLabel: "Sogdo's Edge",
      sectionTitle: "What Sets Us",
      sectionTitleHighlight: "Apart",
      sectionDescription: "Our unique approach combines industry expertise, cutting-edge technology, and proven methodologies to deliver exceptional results.",
      steps: [
        {
          number: "01",
          title: "Industry-Specific Teams",
          description: "Dedicated teams with deep expertise in each industry vertical, ensuring solutions that truly understand your business domain and challenges."
        },
        {
          number: "02",
          title: "Modular, Scalable Platforms",
          description: "Built with modularity in mind, our platforms can scale seamlessly as your business grows, adapting to changing requirements and increasing demands."
        },
        {
          number: "03",
          title: "Integration with APIs & Legacy Systems",
          description: "Seamless integration capabilities that work with your existing systems, APIs, and legacy infrastructure, minimizing disruption and maximizing value."
        },
        {
          number: "04",
          title: "Built for Speed & Scale",
          description: "Architecture and development practices optimized for rapid deployment and enterprise-scale performance, ensuring your solutions can handle growth."
        }
      ]
    });

    onMounted(async () => {
      // Initialize WOW.js
      new WOW({
        mobile: false,
        live: false
      }).init();
    });

    return {
      synergySlides,
      synergyMissionVision,
      productCategoriesContent,
      featuredProductsContent,
      developmentProcessContent
    };
  }
}
</script>

<style scoped>
/* Import Google Fonts */
@import url("https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap");

/* Import Flaticon */
@import "@/assets/scss/Flaticon.scss";

/* Global Styles */
body {
  font-family: "Open Sans", sans-serif;
  line-height: 1.6;
  color: #333;
}

.see__pad {
  padding: 100px 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

/* Pattern Layers */
.pattern-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.pattern-1 {
  top: 10%;
  left: 5%;
  width: 100px;
  height: 100px;
  opacity: 0.1;
}

.pattern-2 {
  top: 20%;
  right: 10%;
  width: 80px;
  height: 80px;
  opacity: 0.1;
}

.pattern-3 {
  bottom: 20%;
  left: 15%;
  width: 120px;
  height: 120px;
  opacity: 0.1;
}

.pattern-4 {
  bottom: 10%;
  right: 5%;
  width: 90px;
  height: 90px;
  opacity: 0.1;
}

/* Home Synergy page specific styles */
.home-synergy-page section.banner__two {
  padding-top: 100px !important;
  padding-bottom: 100px !important;
}

@media (max-width: 768px) {
  .home-synergy-page section.banner__two {
    padding-top: 80px !important;
    padding-bottom: 80px !important;
  }
}

@media (max-width: 480px) {
  .home-synergy-page section.banner__two {
    padding-top: 60px !important;
    padding-bottom: 60px !important;
  }
}
</style>
